/**
 * Data Corruption Fragment Shader
 * Chromatic aberration + digital noise + scanlines
 *
 * Note: This file contains the GLSL source as a string for runtime compilation
 */

export const DataCorruptionFragShader = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform sampler2D uMainSampler;
uniform float uTime;
uniform float uIntensity;
uniform vec2 uResolution;

varying vec2 outTexCoord;

// Pseudo-random function
float random(vec2 st) {
    return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
}

// Digital noise
float noise(vec2 st) {
    vec2 i = floor(st);
    vec2 f = fract(st);

    float a = random(i);
    float b = random(i + vec2(1.0, 0.0));
    float c = random(i + vec2(0.0, 1.0));
    float d = random(i + vec2(1.0, 1.0));

    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
}

void main() {
    vec2 uv = outTexCoord;

    // Chromatic aberration based on intensity
    float aberrationAmount = uIntensity * 0.008;

    // Add slight wave distortion
    float wave = sin(uv.y * 40.0 + uTime * 2.0) * uIntensity * 0.002;

    // Sample with chromatic aberration
    float r = texture2D(uMainSampler, uv + vec2(aberrationAmount + wave, 0.0)).r;
    float g = texture2D(uMainSampler, uv + vec2(wave, 0.0)).g;
    float b = texture2D(uMainSampler, uv - vec2(aberrationAmount - wave, 0.0)).b;

    vec3 color = vec3(r, g, b);

    // Digital noise overlay
    float n = noise(uv * uResolution * 0.3 + uTime * 8.0);
    float noiseIntensity = uIntensity * 0.12;
    color = mix(color, vec3(n), noiseIntensity);

    // Random horizontal glitch lines
    float lineNoise = random(vec2(floor(uv.y * 80.0), floor(uTime * 8.0)));
    if (lineNoise > (1.0 - uIntensity * 0.08)) {
        vec2 glitchUV = uv;
        glitchUV.x += (random(vec2(uTime, uv.y)) - 0.5) * 0.08 * uIntensity;
        color = texture2D(uMainSampler, glitchUV).rgb;
    }

    // Scanline effect
    float scanline = sin(uv.y * uResolution.y * 1.2) * 0.03 * uIntensity;
    color -= scanline;

    // Vignette darkness at edges (increases with intensity)
    float vignette = 1.0 - length(uv - 0.5) * uIntensity * 0.4;
    color *= max(vignette, 0.5);

    // Subtle red tint at high intensity (danger indication)
    if (uIntensity > 0.5) {
        float redTint = (uIntensity - 0.5) * 0.15;
        color.r = min(color.r + redTint, 1.0);
    }

    gl_FragColor = vec4(color, 1.0);
}
`;
